import { useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CohortViewHeader from "../../components/CohortViewHeader/CohortViewHeader";
import { SCREENS, cohortStaticData, PROGRESS_ACCORDION_IDS } from "../../utils/CommonUtils/constants";
import studentBothAssessmentsImg from "../../assets/student_in_both_assessments.svg";
import infoIconItalics from "../../assets/info_icon_italics.svg";
import upArrowIcon from "../../assets/up-arrow.svg";
import downArrowIcon from "../../assets/down-arrow.svg";
import "../../components/CohortDetailsContent/CohortDetailsContent.css";
import "./ComparativeAssessmentScreen.css";
import { produceEventLog, getUserProperties } from "../../utils/helper";
import useParamValues from "../../utils/useParamValues";
import { buildComparativeViewFromStudents, formatCwpmDelta } from "./buildComparativeViewFromStudents";
import celebrationEmoji from "../../assets/celebrate_footer.svg";
import useTranslationSetup from "../../utils/useTranslationSetup";

const COHORT_ORDER = [1, 2, 3, 4];

const CURRENT_BAR_COLORS = {
  1: "#ff649b",
  2: "#ffb484",
  3: "#5c8dff",
  4: "#66cb00",
};

const CHART_MAX_PX = 120;

function cwpmTrendIcon(previousCwpm, currentCwpm) {
  const cwpmChange = currentCwpm - previousCwpm;
  if (cwpmChange >= 0) {
    return { src: upArrowIcon, alt: "CWPM बढ़ा" };
  }
  return { src: downArrowIcon, alt: "CWPM घटा" };
}

function ComparativeBarChart({ previousCounts, currentCounts }) {
  const vals = COHORT_ORDER.flatMap((id) => [
    previousCounts[id],
    currentCounts[id],
  ]);
  const maxVal = Math.max(1, ...vals);

  return (
    <Box
      className="comp-chart-groups"
      role="img"
      aria-label="तुलनात्मक आकलन चार्ट"
    >
      {COHORT_ORDER.map((id) => {
        const data = cohortStaticData[id];
        const prevVal = previousCounts[id];
        const currVal = currentCounts[id];
        const color = CURRENT_BAR_COLORS[id];
        const prevH = (prevVal / maxVal) * CHART_MAX_PX;
        const currH = (currVal / maxVal) * CHART_MAX_PX;

        return (
          <Box key={id} className="comp-chart-group">
            <Box className="comp-bar-pair">
              <Box
                className="comp-bar"
                style={{
                  height: Math.max(prevH, 2),
                  backgroundColor: "#E4E4E3",
                }}
              />
              <Box
                className="comp-bar"
                style={{
                  height: Math.max(currH, 2),
                  backgroundColor: color,
                }}
              />
            </Box>
            <Box className="comp-bar-values">
              <Typography component="span" className="comp-bar-value">
                {prevVal}
              </Typography>
              <Typography component="span" className="comp-bar-value">
                {currVal}
              </Typography>
            </Box>
            <Box className="comp-chart-label-row">
              <img src={data.icon} alt="" className="comp-chart-cohort-icon" />
              <Typography className="comp-chart-cohort-label" style={{ color }}>
                {data.title}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function ComparativeAccordionStudentRow({
  name,
  className,
  previousCwpm,
  currentCwpm,
  cwpmChange,
}) {
  const { src, alt } = cwpmTrendIcon(previousCwpm, currentCwpm);

  return (
    <Box className="student-list-item comp-accordion-student-row">
      <Box className={`student-color-indicator ${className}-border`} />
      <Box className="student-info comp-student-info-with-trend">
        <Typography className="cohort-student-name">{name}</Typography>
        <Box className="comp-student-trend">
          <img src={src} alt={alt} className="comp-student-cwpm-arrow" />
          <Typography component="span" className="comp-student-cwpm-delta">
            {formatCwpmDelta(cwpmChange)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ComparativeAccordionBlock({ acc, t }) {
  const borderClass =
    acc.variant === "pink" ? "comp-accordion--pink" : "comp-accordion--green";
  const titleClass =
    acc.variant === "pink"
      ? "comp-accordion-title--pink"
      : "comp-accordion-title--green";
  const count = acc.students.length;
  const headingLine = `${count} ${t("students")}`;

  return (
    <Accordion
      className={`comp-accordion ${borderClass}`}
      disableGutters
      elevation={0}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className="comp-expand-icon" />}
      >
        <Box className="comp-accordion-summary-stack">
          <Typography
            className={`comp-accordion-title ${titleClass}`}
            component="span"
          >
            {headingLine}
          </Typography>
          <Typography className="comp-accordion-subline" component="span">
            {acc.subline}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box className="comp-accordion-student-list">
          {acc.students.map((student) => (
            <ComparativeAccordionStudentRow
              key={student.student_id}
              name={student.name}
              className={student.className}
              previousCwpm={student.previousCwpm}
              currentCwpm={student.currentCwpm}
              cwpmChange={student.cwpmChange}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

const ComparativeAssessmentScreen = ({
  totalStudents,
  comparisonStudents,
  setActiveScreen,
}) => {
  const userProperties = getUserProperties(useParamValues());
  const { t } = useTranslationSetup();

  const view = useMemo(
    () => buildComparativeViewFromStudents(comparisonStudents ?? [], totalStudents, t),
    [comparisonStudents],
  );

  const progressAccordions = view.accordions.filter(
    (a) => PROGRESS_ACCORDION_IDS.has(a.id) && a.students.length > 0,
  );

  const regressedAccordion = view.accordions.find(
    (a) => a.id === "regressed" && a.students.length > 0,
  );

  const goToCohortMain = () => {
    setActiveScreen(SCREENS.RENDER_COHORT_VIEW_SCREEN);
  };

  useEffect(() => {
    produceEventLog("results_2b_comparative_assessment_view", {
      ...userProperties,
      class_student_count: totalStudents,
      students_with_both_comparative: view.studentsWithBothAssessments,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Box className="cohort-main-screen-header-wrapper">
        <CohortViewHeader
          showBack
          onBack={goToCohortMain}
        />
      </Box>

      <Box className="comp-screen-body">
        <Box className="comp-hero" component="header">
          <Box className="comp-hero-gradient-layer">
            <img
              src={studentBothAssessmentsImg}
              alt=""
              className="comp-hero-full-image"
              aria-hidden
            />

            <Box className="comp-hero-text-overlay">
              <Typography className="comp-hero-count" component="h1">
                {view.studentsWithBothAssessments}
                <span className="comp-hero-count-numbers-total">
                  /{view.totalStudents}
                </span>{" "}
                <span className="comp-hero-count-word">{t("students")}</span>
              </Typography>

              <Typography className="comp-hero-sub">
                {t("compare_previous_current_description")}
              </Typography>
            </Box>

            <Box className="comp-hero-screen-inset" role="note">
              <img
                src={infoIconItalics}
                alt=""
                className="comp-hero-screen-inset-icon"
                width={20}
                height={20}
                aria-hidden
              />
              <Typography className="comp-hero-screen-inset-text">
                {t("student_not_assessed_desc")}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className="comp-screen-inset comp-hero-chart-wrap">
          <Box component="section" aria-labelledby="comp-chart-heading">
            <Typography
              id="comp-chart-heading"
              className="comp-section-title"
              component="h2"
            >
              {t("comparative_assessment")}
            </Typography>
            <Typography className="comp-section-subtitle">
              ({t("previous_to_current")})
            </Typography>

            <Box className="comp-chart-legend">
              <Box className="comp-chart-legend-item">
                <span className="comp-legend-dot-grey" aria-hidden />
                <Typography className="comp-legend-label">
                  {t("previous")}{" "}{t("assessment")}
                </Typography>
              </Box>
              <Box className="comp-chart-legend-item">
                <span className="comp-legend-dots" aria-hidden>
                  <span style={{ background: CURRENT_BAR_COLORS[1] }} />
                  <span style={{ background: CURRENT_BAR_COLORS[2] }} />
                  <span style={{ background: CURRENT_BAR_COLORS[3] }} />
                  <span style={{ background: CURRENT_BAR_COLORS[4] }} />
                </span>
                <Typography className="comp-legend-label">
                  {t("current")}{" "}{t("assessment")}
                </Typography>
              </Box>
            </Box>

            <ComparativeBarChart
              previousCounts={view.previousCounts}
              currentCounts={view.currentCounts}
            />
          </Box>
        </Box>

        {progressAccordions.length > 0 && (
          <Box
            className="comp-accordion-group-positive"
            component="section"
            aria-label="प्रगति समूह"
          >
            <Box className="comp-screen-inset comp-accordion-group-positive-inner">
              {progressAccordions.map((acc) => (
                <ComparativeAccordionBlock key={acc.id} acc={acc} t={t} />
              ))}
            </Box>
          </Box>
        )}

        <Box className="comp-screen-inset comp-accordions-regressed">
          {regressedAccordion && (
            <ComparativeAccordionBlock acc={regressedAccordion} t={t} />
          )}

          <Box className="comp-footer-card" component="footer">
            <span
              className="comp-footer-emoji"
              role="img"
              aria-label="celebration"
            >
              <img src={celebrationEmoji} alt="celebration" />
            </span>
            <Box className="comp-footer-text-container">
              <Typography className="comp-footer-title">
                {t("congratulation_message")}
              </Typography>
              <Typography className="comp-footer-text">
                {t("congratulation_message_desc")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ComparativeAssessmentScreen;
